/**
 * @file languagePairs.ts
 * @brief Merge language-pair rows that differ only by letter case.
 *
 * @details
 * Sessions store the language as sent by each client, so one pair can come
 * back as two rows ("en"/"EN", "en-GB"/"en-gb"). The table upper-cases both,
 * so they showed as two identical rows (DES 03-10-2026 screenshot: EN → EN
 * twice, EN-GB → UR-PK twice). Rows are merged on the lower-cased codes:
 * sessions and seconds are summed, so the totals are unchanged. The spelling
 * shown is the one with the most sessions.
 *
 * @author DES
 * @date 03-10-2026
 */

export interface LanguagePairRow {
  source_language?: string | null;
  target_language?: string | null;
  session_count?: number | string | null;
  total_duration_seconds?: number | string | null;
}

export function mergeLanguagePairs<T extends LanguagePairRow>(rows: readonly T[]): T[] {
  const merged = new Map<string, { row: T; lead: number }>();
  for (const row of rows) {
    const key = `${(row.source_language ?? "").toLowerCase()}>${(row.target_language ?? "").toLowerCase()}`;
    const sessions = Number(row.session_count || 0);
    const seconds = Number(row.total_duration_seconds || 0);
    const seen = merged.get(key);
    if (!seen) {
      merged.set(key, { row: { ...row, session_count: sessions, total_duration_seconds: seconds }, lead: sessions });
      continue;
    }
    const total = {
      session_count: Number(seen.row.session_count || 0) + sessions,
      total_duration_seconds: Number(seen.row.total_duration_seconds || 0) + seconds,
    };
    seen.row =
      sessions > seen.lead
        ? { ...row, ...total }
        : { ...seen.row, ...total };
    seen.lead = Math.max(seen.lead, sessions);
  }
  return [...merged.values()]
    .map((m) => m.row)
    .sort((a, b) => Number(b.session_count || 0) - Number(a.session_count || 0));
}
